/**
 * Teslyn Cloud's left menu: the staff workspace's pages, the client portal's and Braynewave's console's, each as the
 * person's roles allow (Shell). It behaves as Teslyn Field's (apps/field/src/components/SideNav.tsx, MB 25 Oct 2026:
 * "the same collapse behaviour as the Field app including default to icons only mode"); keep the two the same.
 *
 * - Pinned (stored as `expanded`): icons and names, always open. On an iPad in portrait (600 to 899 px) it behaves
 *   as icons only: the column of icons down the left (decision 1030, cloud-side-menu; it was a row across the top).
 * - Icons only (the default): one icon wide; each item keeps its name for screen readers and as its tooltip. It opens
 *   over the page with the names while the mouse is on it (after MENU_OPEN_DELAY_MS, closing MENU_CLOSE_DELAY_MS
 *   after it leaves), while keyboard focus is in it, or after a tap; Escape or a tap outside closes it.
 * - Headings (C9, decision 855): each group of pages under a small heading; a thin divider while icons only.
 * - Hidden: the Shell draws the menu only while the top bar's menu button has opened it, with the names and over the
 *   page; it goes back to hidden on choosing a page, the mouse leaving, Escape, a click outside or Tab past it.
 * - On a phone (under 600 px, decision 1030, cloud-side-menu) the menu is always that: the Shell's "Menu" button at
 *   the top left opens the whole menu with the names over the page (`drawer`), and choosing a page closes it. The
 *   person's own choice (pinned, icons only, hidden) is kept for wider screens.
 */
import {
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactElement,
} from 'react';
import { MENU_CLOSE_DELAY_MS, type MenuMode } from '@teslyn/domain';
import { Icon, type IconName } from '../components/Icon.tsx';
import { MenuModeControl } from '../components/MenuMode.tsx';
import { useHoverOpen } from '../components/useHoverOpen.ts';
import { NARROW_QUERY, useMediaQuery } from '../components/useMediaQuery.ts';

/** Marks the top bar's menu button: pressing it is not "a click outside" the menu it opens and closes. */
export const MENU_TOGGLE_ATTR = 'data-menu-toggle';

/** Why a menu opened from hidden goes back: Escape gives focus back to the top bar's menu button. */
export type MenuDismiss = 'leave' | 'chosen' | 'escape' | 'outside' | 'focus';

export interface SideNavItem<Id extends string> {
  readonly id: Id;
  readonly label: string;
  readonly icon: IconName;
  /**
   * The small heading drawn before this item, the first of its group (C9, decision 855); a thin divider while the
   * menu is icons only. Home at the top has none.
   */
  readonly heading?: string;
}

export interface SideNavProps<Id extends string> {
  /** The menu's accessible name: "Workspace", "Client portal" or "Platform". */
  readonly label: string;
  readonly items: readonly SideNavItem<Id>[];
  readonly current: Id;
  readonly onSelect: (id: Id) => void;
  readonly mode: MenuMode;
  readonly onMode?: (m: MenuMode) => void;
  /** Hidden, opened from its button: the menu goes back to hidden. */
  readonly onDismiss?: (why: MenuDismiss) => void;
  /**
   * A phone (decision 1030): drawn only while the top bar's "Menu" button has opened it, as hidden is, whatever the
   * person's choice.
   */
  readonly drawer?: boolean;
  readonly id?: string;
}

/** Focus arriving this soon after a press in the menu came from that press, not from the keyboard. */
const POINTER_FOCUS_MS = 500;

const isToggle = (t: EventTarget | null): boolean =>
  t instanceof Element && t.closest(`[${MENU_TOGGLE_ATTR}]`) !== null;

export function SideNav<Id extends string>({
  label,
  items,
  current,
  onSelect,
  mode,
  onMode,
  onDismiss,
  drawer = false,
  id,
}: SideNavProps<Id>): ReactElement {
  // decision 1030 (replacing 786's row of icons): on an iPad in portrait the menu is the column of icons, opening
  // with the names over the page; "Pinned" keeps its name and comes back on a wide window. On a phone it is the
  // drawer opened from the top bar (`drawer`), drawn as hidden's.
  const narrow = useMediaQuery(NARROW_QUERY);
  const hidden = mode === 'hidden' || drawer;
  const icons = !hidden && (mode === 'icons' || (mode === 'expanded' && narrow));
  const nav = useRef<HTMLElement>(null);
  // icons only: why it is open (the mouse after its delay, keyboard focus, a tap)
  const hoverOpen = useHoverOpen(icons);
  const hover = hoverOpen.open;
  const closeHover = hoverOpen.close;
  const [focused, setFocused] = useState(false);
  const [tapped, setTapped] = useState(false);
  // the Menu choices at the foot are open: the menu stays open under them, and Escape closes them first
  const [choices, setChoices] = useState(false);
  const choicesOpen = useRef(false);
  const timer = useRef<number | undefined>(undefined);
  // a mouse or a finger pressed in the menu: the focus that follows is not keyboard focus (nor the focus the Menu
  // control takes back just after a choice was pressed, which kept the drawer open on a tablet)
  const pressed = useRef(false);
  const lastPress = useRef(0);
  // a tap that opened icons only does not also choose the icon tapped
  const swallow = useRef(false);
  // another mode chosen: the menu starts afresh (closed icons, no pending delay)
  const [seen, setSeen] = useState(mode);
  if (seen !== mode) {
    setSeen(mode);
    setFocused(false);
    setTapped(false);
  }
  const peek = icons && (hover || focused || tapped || choices);

  const dismiss = useRef(onDismiss);
  useEffect(() => {
    dismiss.current = onDismiss;
  });
  const clear = (): void => {
    window.clearTimeout(timer.current);
    timer.current = undefined;
  };
  const later = (ms: number, fn: () => void): void => {
    clear();
    timer.current = window.setTimeout(fn, ms);
  };
  useEffect(() => () => window.clearTimeout(timer.current), [mode]);

  // opened from hidden: focus on the open page's item, so the keyboard carries on in the menu
  useEffect(() => {
    if (!hidden) return;
    nav.current?.querySelector<HTMLElement>('[aria-current="page"]')?.focus();
  }, [hidden]);

  // a click or tap outside, or Escape, closes an opened menu
  const open = peek || hidden;
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: Event): void => {
      if (nav.current?.contains(e.target as Node) || isToggle(e.target)) return;
      setTapped(false);
      if (hidden) dismiss.current?.('outside');
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || choicesOpen.current) return;
      window.clearTimeout(timer.current);
      closeHover();
      setFocused(false);
      setTapped(false);
      if (hidden) dismiss.current?.('escape');
    };
    document.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, hidden, closeHover]);

  function onPointerEnter(e: PointerEvent): void {
    if (e.pointerType === 'touch') return;
    if (icons) hoverOpen.onPointerEnter(e);
    else if (hidden) clear();
  }
  function onPointerLeave(e: PointerEvent): void {
    pressed.current = false;
    if (e.pointerType === 'touch') return;
    if (icons) hoverOpen.onPointerLeave(e);
    else if (hidden) later(MENU_CLOSE_DELAY_MS, () => dismiss.current?.('leave'));
  }
  function onPointerDown(e: PointerEvent): void {
    pressed.current = true;
    lastPress.current = Date.now();
    swallow.current = icons && !peek && e.pointerType === 'touch';
    if (swallow.current) setTapped(true);
  }
  function onClickCapture(e: MouseEvent): void {
    if (!swallow.current) return;
    swallow.current = false;
    e.preventDefault();
    e.stopPropagation();
  }
  function onFocus(): void {
    const byPointer = pressed.current || Date.now() - lastPress.current < POINTER_FOCUS_MS;
    pressed.current = false;
    if (icons && !byPointer) setFocused(true);
  }
  function onBlur(e: FocusEvent): void {
    const to = e.relatedTarget;
    if (to instanceof Node && nav.current?.contains(to)) return;
    setFocused(false);
    // focus moved on by the keyboard (Tab past the menu); the top bar's button closes the menu itself
    if (hidden && to !== null && !isToggle(to)) dismiss.current?.('focus');
  }
  function choose(item: Id): void {
    onSelect(item);
    // opened by a tap (a drawer on a phone): choosing a page closes it, as choosing from hidden does
    setTapped(false);
    if (hidden) dismiss.current?.('chosen');
  }

  const names = !icons || peek;
  const className = [
    'sidenav',
    icons ? 'sidenav--icons' : '',
    peek ? 'sidenav--peek' : '',
    hidden ? 'sidenav--overlay' : '',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <nav
      ref={nav}
      id={id}
      className={className}
      aria-label={label}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPointerDown={onPointerDown}
      onPointerUp={() => (pressed.current = false)}
      onClickCapture={onClickCapture}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      <ul className="sidenav__list">
        {items.flatMap((item) => [
          ...(item.heading
            ? [
                <li key={`h:${item.heading}`} className="sidenav__heading">
                  <h2 className="caption sidenav__heading-label">{item.heading}</h2>
                </li>,
              ]
            : []),
          <li key={item.id}>
            <button
              type="button"
              className={item.id === current ? 'sidenav__item sidenav__item--on' : 'sidenav__item'}
              aria-current={item.id === current ? 'page' : undefined}
              title={names ? undefined : item.label}
              onClick={() => choose(item.id)}
            >
              <Icon name={item.icon} />
              <span className="sidenav__label">{item.label}</span>
            </button>
          </li>,
        ])}
      </ul>
      {onMode && (
        <MenuModeControl
          mode={mode}
          onChange={(m) => {
            // the choice list leaves under the pointer without a pointerout, so the hover-open would stay: close it
            // (the mouse coming back opens it again as usual)
            closeHover();
            onMode(m);
          }}
          onOpenChange={(o) => {
            choicesOpen.current = o;
            setChoices(o);
          }}
        />
      )}
    </nav>
  );
}
